/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useRef } from "react";
import { TouchableOpacity, View } from "react-native";
import LottieView from "lottie-react";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";

import { FadeInOutView, LoadingDots, Text, useTimerRef } from "UpduoLibrary";
import { skalePrimaryBlue, skaleLightRed } from "config/UpduoColors";
import { formattedTimeString } from "config/Utilities";
import matchLottie from "assets/match_lottie.json";
import { RootState } from "store/types";

const matchScreenTextArray = [
  "Notifying potential partners...",
  "Tip: For a smooth session, make sure you have good internet connection.",
  "Tip: Check that you have good lighting.",
  "Tip: Don't worry if you feel shy or nervous; your partner probably is too.",
  "Notifying more potential partners...",
  "Be patient; your peers may be busy!",
  "Seems like your peers are busy, we'll notify you when someone joins!",
];

interface MatchProps {
  onCancel: Function;
  queueInProgress: boolean;
  onMatchStatusQueueCheck: Function;
}

export default function Match({
  onCancel,
  queueInProgress,
  onMatchStatusQueueCheck,
}: MatchProps) {
  const dispatch = useDispatch();

  const matchAck = useSelector((state: RootState) => state.matchAck);

  const [isPartnerAckTimerActive, setIsPartnerAckTimerActive] = useState(false);

  const [partnerAckSecondsRemaining, setPartnerAckSecondsRemaining] =
    useState(120);
  const partnerAckSecondsRemainingRef = useRef<number>(120);
  partnerAckSecondsRemainingRef.current = partnerAckSecondsRemaining;

  const [textIndex, setTextIndex] = useState(-1);
  const [isTextVisible, setIsTextVisible] = useState(false);

  const [setPartnerAckTimer, clearPartnerAckTimer] = useTimerRef();
  const [setTextTimer, clearTextTimer] = useTimerRef();

  useEffect(() => {
    return () => {
      _handleRemovePartnerAckTimer();
      _handleRemoveTextTimer();
    };
  }, []);

  useEffect(() => {
    if (matchAck) {
      _handleCreatePartnerAckTimer();
    } else {
      _handleRemovePartnerAckTimer();
    }
  }, [matchAck]);

  useEffect(() => {
    if (!queueInProgress && !matchAck) {
      _handleShowFadeText();
      _handleCreateTextTimer();
    } else {
      _handleRemoveTextTimer();
    }
  }, [queueInProgress, matchAck]);

  const _handleQueueCancel = () => {
    dispatch(ActionCreators.setIsQueueBeaconActivated(false));
    onCancel();
  };

  const _handleCreatePartnerAckTimer = () => {
    // When the app is on and status in app is online,
    // check every 30 seconds for whether online status is correct
    _handleRemovePartnerAckTimer();
    setIsPartnerAckTimerActive(true);
    setPartnerAckTimer(
      setInterval(() => {
        if (partnerAckSecondsRemainingRef.current < 0) {
          _handleRemovePartnerAckTimer();
          onMatchStatusQueueCheck();
        } else {
          setPartnerAckSecondsRemaining((previousState) => previousState - 1);
        }
      }, 1000)
    );
  };

  const _handleRemovePartnerAckTimer = () => {
    // Clear the partner ack timer
    clearPartnerAckTimer();
    setIsPartnerAckTimerActive(false);
    setPartnerAckSecondsRemaining(120);
  };

  const _handleCreateTextTimer = () => {
    // Run _handleShowFadeText both when the user is in the queue but not yet matched,
    // and then for subsequent loops
    _handleRemoveTextTimer();
    setTextTimer(setInterval(_handleShowFadeText, 6000));
  };

  const _handleRemoveTextTimer = () => {
    // Clear the partner ack timer
    clearTextTimer();
    setIsTextVisible(false);
    setTextIndex(-1);
  };
  const _handleShowFadeText = () => {
    // Run at 0 ms
    setTextIndex(
      (previousState) => (previousState + 1) % matchScreenTextArray.length
    );
    setIsTextVisible(true);
    // Run at 4900 ms (leave 1.1 seconds with no text to allow readers some breathing space)
    setTimeout(() => {
      setIsTextVisible(false);
    }, 4900);
  };

  let message: string;
  if (matchAck) {
    message = "Match found! Your partner is getting ready...";
  } else if (queueInProgress) {
    message = "Connecting, please wait...";
  } else {
    message =
      "You are now in the queue!\nYou'll be matched when someone joins.";
  }
  const textChange = matchScreenTextArray[textIndex];
  const partnerAckTimerDisplay = (
    <Text
      style={{
        fontWeight: "bold",
        fontSize: 16,
      }}
    >
      {formattedTimeString(Math.max(partnerAckSecondsRemaining, 0))}
    </Text>
  );
  return (
    <View
      style={{
        flex: 1,
        height: "95%",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
      }}
    >
      <View
        style={{
          flex: 1,
          alignItems: "center",
        }}
      >
        <View
          style={{
            alignItems: "center",
            height: 120,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginTop: 50,
            }}
          >
            {isPartnerAckTimerActive ? null : (
              <LoadingDots color={skalePrimaryBlue} isAlternate={true} />
            )}
            <Text
              style={{
                margin: 15,
                fontSize: 16,
              }}
            >
              {message}
            </Text>
          </View>
          {isPartnerAckTimerActive && matchAck ? partnerAckTimerDisplay : null}
        </View>
        <View
          style={{
            height: 50,
            justifyContent: "center",
          }}
        >
          <TouchableOpacity
            style={{ padding: 25, justifyContent: "center" }}
            onPress={() => _handleQueueCancel()}
          >
            <Text
              isHeading={true}
              style={{
                textTransform: "uppercase",
                textAlign: "center",
                color: skaleLightRed,
              }}
            >
              {"Cancel"}
            </Text>
          </TouchableOpacity>
        </View>
        <View
          style={{
            height: 140,
            width: 400,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FadeInOutView
            style={{ justifyContent: "center" }}
            duration={1000}
            visible={isTextVisible}
          >
            <Text
              style={{
                fontSize: 16,
                color: skalePrimaryBlue,
                textAlign: "center",
              }}
            >
              {textChange}
            </Text>
          </FadeInOutView>
        </View>
      </View>
      <View
        style={{
          flex: 1,
          alignItems: "center",
        }}
      >
        <LottieView
          animationData={matchLottie}
          style={{ width: 400, height: 400, margin: 30 }}
        />
      </View>
    </View>
  );
}
